import React, { useEffect, useState, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";
import Logo from "../assets/images/Logo.png";

const Header = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const offcanvasRef = useRef(null);

  // Navigation links data
  const navigationLinks = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About Us" },
    { to: "/services", label: "Services" },
    { to: "/event", label: "Events" },
    { to: "/deities", label: "Deities" },
    { to: "/contactus", label: "Contact" },
  ];

  // Contact information data
  const contactInfo = [
    {
      icon: FaPhoneAlt,
      label: "Talk to us",
      value: "0296312800",
      href: "tel:0296312800",
      className: "text-start",
    },
    {
      icon: FaEnvelope,
      label: "Email to us",
      value: "secretary@sydneysriayyappa.com",
      href: "mailto:secretary@sydneysriayyappa.com",
      className: "",
    },
    {
      icon: FaMapMarkerAlt,
      label: "Location",
      value: "1 Perry Street, Wentworthville, NSW, Australia",
      href: "https://www.google.com/maps/place/Sydney+Sri+Ayyappa+Swami+Centre/@-33.8106082,150.9655676,17z/data=!3m1!4b1!4m6!3m5!1s0x6b12bd63e38aea17:0xe10180d0690ad68f!8m2!3d-33.8106083!4d150.9704385!16s%2Fg%2F11xk3why1?entry=ttu&g_ep=EgoyMDI1MDgxOS4wIKXMDSoASAFQAw%3D%3D",
      className: "text-start",
      target: "_blank",
      rel: "noopener noreferrer",
    },
  ];

  useEffect(() => {
    if (showOffcanvas) {
      // prevent background page from scrolling
      document.body.style.overflow = "hidden";
    } else {
      // restore normal scrolling
      document.body.style.overflow = "";
    }

    // Cleanup in case the component unmounts while menu is open
    return () => {
      document.body.style.overflow = "";
    };
  }, [showOffcanvas]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 992);
    };

    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 150);
    };

    // Handle escape key to close offcanvas
    const handleEscape = (e) => {
      if (e.key === "Escape" && showOffcanvas) {
        handleOffcanvasClose();
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showOffcanvas]);

  const handleOffcanvasToggle = () => {
    setShowOffcanvas(!showOffcanvas);
  };

  const handleOffcanvasClose = () => {
    setShowOffcanvas(false);
  };

  // Close offcanvas when clicking on backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleOffcanvasClose();
    }
  };

  return (
    <div className="container-fluid p-0 bg-white">
      <div className="fixed-top">
        {/* Top contact strip (desktop only) */}
        <section
          className={`bg-white d-none d-lg-block py-2 ${
            isScrolled ? "d-none" : ""
          }`}
        >
          <div className="container">
            <div className="row">
              <div className="d-flex justify-content-between align-items-center">
                {contactInfo.map((contact, index) => {
                  const IconComponent = contact.icon;
                  return (
                    <div key={index} className="col d-flex col-4">
                      <IconComponent className="me-2 icon-style heading rounded-3 p-2 mt-2" />
                      <div className="contact-info">
                        <a
                          href={contact.href}
                          className={contact.className}
                          target={contact.target}
                          rel={contact.rel}
                        >
                          <span className="text-muted small">
                            {contact.label}
                          </span>
                          <br />
                          <span className="text-dark small">
                            {contact.value}
                          </span>
                        </a>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Main Navigation */}
        <nav
          className={`full-width-background navbar navbar-expand-lg ${
            isScrolled ? "fixed-navbar" : ""
          }`}
        >
          <div className="container d-flex justify-content-between align-items-center">
            {/* Mobile Logo */}
            <Link
              to="/"
              className="navbar-brand d-flex align-items-center d-block d-lg-none py-2"
            >
              <img
                src={Logo}
                alt="Sydney Sri Ayyappa Swami Centre"
                className="logo"
              />
            </Link>

            <button
              className="navbar-toggler d-flex align-items-end justify-content-end d-block d-lg-none border-white border-1"
              type="button"
              onClick={handleOffcanvasToggle}
              aria-expanded={showOffcanvas}
              aria-controls="offcanvasNavbar"
              aria-label="Toggle navigation menu"
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: "transparent",
              }}
            >
              <span
                className="navbar-toggler-icon"
                style={{
                  width: "40px",
                  height: "40px",
                  backgroundSize: "30px",
                }}
              ></span>
            </button>

            {/* Desktop Navigation */}
            <div className="collapse navbar-collapse" id="navbarNav">
              <div className="d-flex justify-content-between align-items-center w-100 nav-links">
                <div className="d-flex align-items-center justify-content-start">
                  <Link
                    to="/"
                    className="navbar-brand d-flex align-items-center d-none d-lg-flex"
                  >
                    <img
                      src={Logo}
                      alt="Sydney Sri Ayyappa Swami Centre"
                      className="logo"
                    />
                  </Link>
                </div>

                <div className="d-flex align-items-center gap-4">
                  {navigationLinks.map((link) => (
                    <NavLink
                      key={link.to}
                      to={link.to}
                      className="text-white nav-link-custom"
                    >
                      {link.label}
                    </NavLink>
                  ))}
                </div>
                <NavLink
                  to="/donation"
                  className="btn btn-warning px-4 py-3  hover-button text-white fw-bold border-0 position-relative rounded-5 d-none d-lg-block"
                >
                  Donation
                </NavLink>
              </div>
            </div>
          </div>
        </nav>

        {/* Offcanvas Menu for Mobile */}
        {showOffcanvas && (
          <div
            className="offcanvas-backdrop fade show"
            onClick={handleBackdropClick}
            style={{ cursor: "pointer" }}
          />
        )}

        <div
          ref={offcanvasRef}
          className={`offcanvas offcanvas-end offcanvas-custom text-white d-flex flex-column justify-content-center align-items-center text-center text-white nav-link-custom w-100 fw-bold ${
            showOffcanvas ? "show" : ""
          }`}
          tabIndex="-1"
          id="offcanvasNavbar"
          aria-labelledby="offcanvasNavbarLabel"
          style={{
            visibility: showOffcanvas ? "visible" : "hidden",
            transform: showOffcanvas ? "translateX(0)" : "translateX(100%)",
          }}
        >
          <div className="offcanvas-header w-100 d-flex justify-content-between align-items-center">
            {/* Logo on the left side */}
            <Link
              to="/"
              className="navbar-brand d-flex align-items-center"
              onClick={handleOffcanvasClose}
            >
              <img
                src={Logo}
                alt="Sydney Sri Ayyappa Swami Centre"
                className="logo"
                style={{ height: "60px" }}
              />
            </Link>

            {/* Close button on the right side */}
            <button
              type="button"
              className="btn-close btn-close-white fs-1 mt-1 me-1"
              onClick={handleOffcanvasClose}
              aria-label="Close"
            ></button>
          </div>

          <div className="offcanvas-body d-flex flex-column justify-content-center text-white nav-link-custom lh-lg">
            {navigationLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className="nav-link-custom text-white nav-link-custom mb-3"
                onClick={handleOffcanvasClose}
              >
                {link.label}
              </NavLink>
            ))}
            <NavLink
              to="/donation"
              className="btn btn-warning rounded-5 px-3 py-3 text-dark fw-bold donation-btn mt-3"
              onClick={handleOffcanvasClose}
            >
              Donation
            </NavLink>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Header;
